import {
  AnalysisStateSchema,
  parseContract,
  type AnalysisDimension,
  type AnalysisState,
  type SemanticModel,
} from '@aibi/contracts';
import {
  canonicalJson,
  collectExpressionReferences,
  isCalculatedColumn,
  isCalculatedDimension,
  isCalculatedMeasure,
  isTemporalDimension,
  modelFingerprint,
  SemanticModelIndex,
  validateSemanticModel,
  type ModelMeasure,
} from '@aibi/semantic-model';
import { err, ok, stableHash, type ErrorDetail, type Result } from '@aibi/shared';
import { describeQueryShape } from '../query-shape.js';
import { findAnalysisDiagnostics } from '../validate-analysis-state.js';
import {
  AnalysisPlanError,
  DiagnosticBag,
  type AnalysisDiagnostic,
  type AnalysisDiagnosticCode,
} from './diagnostics.js';
import { grainSupportsType, measureDataType, resolveFormat } from './field-types.js';
import { planPagination, planSorting, type PlanPaginationOptions } from './pagination.js';
import { planJoins } from './plan-joins.js';
import {
  QUERY_PLAN_VERSION,
  type PlannedCalculatedColumn,
  type PlannedColumn,
  type PlannedDimension,
  type PlannedLineage,
  type PlannedMeasure,
  type PlannedRuleFilter,
  type PlannedTable,
  type QueryPlan,
} from './query-plan.js';
import { resolveComparison } from './resolve-comparison.js';
import {
  DependencyResolver,
  type DependencyClosure,
  type DependencyRef,
} from './resolve-dependencies.js';
import { resolveDrilldown } from './resolve-drilldown.js';
import { resolveFilters } from './resolve-filters.js';

export interface QueryPlanOptions {
  /** Paginacion por offset de la solicitud; sin ella solo aplica el `limit` del estado. */
  readonly pagination?: PlanPaginationOptions;
}

/** Analisis validado y listo para planificar. */
export interface AnalysisValidation {
  readonly state: AnalysisState;
  readonly index: SemanticModelIndex;
  /** Advertencias e informacion (nunca errores). */
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

export interface PlannedAnalysis extends AnalysisValidation {
  readonly plan: QueryPlan;
}

/**
 * Valida un analisis de punta a punta antes de planificar: modelo, estado, referencias,
 * tipos, expresiones, relaciones, agregaciones, ciclos y joins ambiguos.
 *
 * @example
 * const validation = validateAnalysis(rawState, model);
 * if (!validation.ok) validation.error.diagnostics.forEach(show);
 */
export function validateAnalysis(
  state: unknown,
  model: unknown,
  options: QueryPlanOptions = {},
): Result<AnalysisValidation, AnalysisPlanError> {
  const planned = planAnalysis(state, model, options);
  if (!planned.ok) return planned;
  const { state: validState, index, diagnostics } = planned.value;
  return ok({ state: validState, index, diagnostics });
}

/**
 * Construye el plan logico de consulta (sin SQL ni ejecucion). Determinista: el mismo
 * modelo y el mismo estado producen el mismo plan y la misma huella. Nunca devuelve un
 * plan si hay diagnosticos de error.
 *
 * @example
 * const plan = buildQueryPlan(state, model);
 * if (plan.ok) dialect.compile(plan.value);
 */
export function buildQueryPlan(
  state: unknown,
  model: unknown,
  options: QueryPlanOptions = {},
): Result<QueryPlan, AnalysisPlanError> {
  const planned = planAnalysis(state, model, options);
  return planned.ok ? ok(planned.value.plan) : planned;
}

/** Igual que `buildQueryPlan`, devolviendo tambien el estado validado y el indice. */
export function planAnalysis(
  stateInput: unknown,
  modelInput: unknown,
  options: QueryPlanOptions = {},
): Result<PlannedAnalysis, AnalysisPlanError> {
  const fail = (diagnostics: readonly AnalysisDiagnostic[]): Result<never, AnalysisPlanError> =>
    err(new AnalysisPlanError('No se puede planificar el analisis.', diagnostics));

  const modelResult = validateSemanticModel(modelInput);
  if (!modelResult.ok) return fail(modelResult.error.details.map(modelIssueDiagnostic));
  const stateResult = parseContract(AnalysisStateSchema, stateInput, 'contrato Analysis State');
  if (!stateResult.ok) {
    return fail(
      stateResult.error.details.map((detail) => ({
        severity: 'error',
        code: 'invalid-state',
        path: detail.path,
        message: detail.message,
      })),
    );
  }
  const model = modelResult.value;
  const state = stateResult.value;
  const index = new SemanticModelIndex(model);
  const base = findAnalysisDiagnostics(state, index);
  if (base.length > 0) return fail(base);

  const planned = assemblePlan(state, model, index, options);
  return planned.ok
    ? ok({ state, index, plan: planned.value, diagnostics: planned.value.diagnostics })
    : planned;
}

/**
 * Huella de la parte de consulta del plan: excluye presentacion, diagnosticos y la
 * propia huella. Cambiar el tipo de visualizacion no cambia la huella.
 */
export function queryPlanFingerprint(plan: Omit<QueryPlan, 'fingerprint'>): string {
  const content: Partial<QueryPlan> = { ...plan };
  Reflect.deleteProperty(content, 'fingerprint');
  Reflect.deleteProperty(content, 'presentation');
  Reflect.deleteProperty(content, 'diagnostics');
  const text = canonicalJson(content);
  return `${stableHash(text, 7)}${stableHash(`#${text}`, 7)}`;
}

function assemblePlan(
  state: AnalysisState,
  model: SemanticModel,
  index: SemanticModelIndex,
  options: QueryPlanOptions,
): Result<QueryPlan, AnalysisPlanError> {
  const bag = new DiagnosticBag();
  const resolver = new DependencyResolver(index);

  // Drilldown y dimensiones
  const drill = resolveDrilldown(state, index);
  bag.addAll(drill.diagnostics);
  const dimensions: PlannedDimension[] = [];
  for (const effective of drill.dimensions) {
    const resolved = index.resolveDimension(effective.dimensionId);
    if (!resolved) {
      bag.error('missing-reference', `Dimension inexistente "${effective.dimensionId}".`, {
        path: `${effective.path}.dimensionId`,
      });
      continue;
    }
    const { dimension, column } = resolved;
    const defaultGrain =
      !isCalculatedDimension(dimension) && isTemporalDimension(resolved)
        ? (dimension.defaultTimeGrain ?? null)
        : null;
    const timeGrain = effective.timeGrain ?? defaultGrain;
    if (timeGrain !== null && !grainSupportsType(timeGrain, resolved.dataType)) {
      bag.error(
        'invalid-type',
        `El grano "${timeGrain}" no es aplicable a una dimension ${resolved.dataType}.`,
        { path: `${effective.path}.timeGrain` },
      );
    }
    dimensions.push({
      key: effective.key,
      label: effective.label ?? dimension.label ?? dimension.name,
      dimensionId: dimension.id,
      kind: isCalculatedDimension(dimension) ? 'calculated' : 'column',
      tableId: resolved.table.id,
      columnId: column?.id ?? null,
      expression: isCalculatedDimension(dimension) ? dimension.expression : null,
      role: resolved.role,
      dataType: resolved.dataType,
      semanticType: resolved.semanticType ?? null,
      timeGrain,
      format: resolveFormat(dimension.format ?? column?.format, index),
      hierarchy: effective.hierarchy,
      origin: effective.origin,
    });
  }

  // Medidas
  const measures: PlannedMeasure[] = [];
  const measureKeyById = new Map<string, string>();
  state.measures.forEach((analysisMeasure, i) => {
    const measure = index.getMeasure(analysisMeasure.measureId);
    if (!measure) {
      bag.error('missing-reference', `Medida inexistente "${analysisMeasure.measureId}".`, {
        path: `measures.${i}.measureId`,
      });
      return;
    }
    if (!measureKeyById.has(measure.id)) measureKeyById.set(measure.id, analysisMeasure.key);
    const closure = resolver.resolve({ kind: 'measure', id: measure.id });
    const column =
      !isCalculatedMeasure(measure) && measure.columnId !== undefined
        ? index.getColumn(measure.columnId)?.column
        : undefined;
    const format = resolveFormat(measure.format ?? column?.format, index);
    measures.push({
      key: analysisMeasure.key,
      label: analysisMeasure.label ?? measure.label ?? measure.name,
      measureId: measure.id,
      kind: isCalculatedMeasure(measure) ? 'expression' : 'aggregation',
      aggregation: isCalculatedMeasure(measure) ? null : measure.aggregation,
      columnId: isCalculatedMeasure(measure) ? null : (measure.columnId ?? null),
      tableId: homeTableId(measure, index),
      filter: isCalculatedMeasure(measure) ? null : (measure.filter ?? null),
      expression: isCalculatedMeasure(measure) ? measure.expression : null,
      dataType: measureDataType(measure, index),
      semanticType: column?.semanticType ?? null,
      format,
      currency: format?.type === 'currency' ? format.currency : null,
      unit:
        format?.type === 'percent'
          ? '%'
          : format?.type === 'number'
            ? (format.suffix ?? null)
            : null,
      dependsOnMeasureIds: closure.measureIds.filter((id) => id !== measure.id),
    });
  });

  // Filtros, selecciones y rutas de drilldown
  const filterResolution = resolveFilters(
    {
      filters: state.filters,
      selections: state.selections,
      selectionMode: state.interaction.selectionMode,
      pathFilters: drill.pathFilters,
      measureKeys: measureKeyById,
    },
    index,
  );
  bag.addAll(filterResolution.diagnostics);
  const { filters } = filterResolution;

  // Comparacion temporal
  const usedKeys = new Set([...dimensions.map((d) => d.key), ...measures.map((m) => m.key)]);
  const comparisonResolution = resolveComparison(
    state.comparison,
    { dimensions, measureKeys: measures.map((m) => m.key), filters, usedKeys },
    index,
  );
  bag.addAll(comparisonResolution.diagnostics);
  const { comparison } = comparisonResolution;

  // Dependencias transitivas y tablas requeridas
  const measureClosures = measures.map((m) =>
    resolver.resolve({ kind: 'measure', id: m.measureId }),
  );
  const dimensionClosures = dimensions.map((d) =>
    resolver.resolve({ kind: 'dimension', id: d.dimensionId }),
  );
  const filterClosures = filters.map((filter) =>
    resolver.resolve(
      filter.target.kind === 'dimension'
        ? { kind: 'dimension', id: filter.target.dimensionId }
        : { kind: 'measure', id: filter.target.measureId },
    ),
  );
  const comparisonClosure = comparison
    ? resolver.resolve({ kind: 'dimension', id: comparison.dateDimensionId })
    : undefined;
  const queryClosures = [
    ...measureClosures,
    ...dimensionClosures,
    ...filterClosures,
    ...(comparisonClosure ? [comparisonClosure] : []),
  ];
  const requiredTables = unique(queryClosures.flatMap((closure) => closure.tableIds));
  const baseTableId = measures[0]?.tableId ?? dimensions[0]?.tableId ?? requiredTables[0] ?? null;

  // Joins
  const hasAggregates =
    measures.length > 0 || filters.some((filter) => filter.stage === 'aggregate');
  const joinPlan = planJoins(requiredTables, baseTableId, index, { hasMeasures: hasAggregates });
  bag.addAll(joinPlan.diagnostics);
  const joinedTables = new Set(joinPlan.tableIds);
  const joinClosures = joinPlan.joins.flatMap((join) => [
    resolver.resolve({ kind: 'column', id: join.fromColumnId }),
    resolver.resolve({ kind: 'column', id: join.toColumnId }),
  ]);

  // Reglas de negocio de filtrado de filas sobre las tablas del plan
  const ruleFilters: PlannedRuleFilter[] = [];
  const ruleClosures: DependencyClosure[] = [];
  for (const rule of model.businessRules) {
    if (!rule.enabled || rule.rule.type !== 'row-filter') continue;
    const { kind, targetId } = rule.appliesTo;
    if (kind !== 'table' && kind !== 'model') continue;
    if (kind === 'table' && (targetId === undefined || !joinedTables.has(targetId))) continue;
    const refs: DependencyRef[] = collectExpressionReferences(rule.rule.expression).columnIds.map(
      (id) => ({ kind: 'column', id }),
    );
    const closures = refs.map((ref) => resolver.resolve(ref));
    const tables = unique(closures.flatMap((closure) => closure.tableIds));
    if (tables.length === 0 || !tables.every((tableId) => joinedTables.has(tableId))) continue;
    ruleClosures.push(...closures);
    ruleFilters.push({
      ruleId: rule.id,
      tableId: kind === 'table' ? (targetId ?? null) : null,
      expression: rule.rule.expression,
    });
  }

  bag.addAll(resolver.diagnostics);

  // Orden, paginacion y agrupacion
  const paginationResolution = planPagination(state.limit, options.pagination);
  bag.addAll(paginationResolution.diagnostics);
  const sortingResolution = planSorting(
    state.sorting,
    dimensions,
    new Set(measures.map((m) => m.key)),
    paginationResolution.requiresStableOrder,
  );
  bag.addAll(sortingResolution.diagnostics);

  if ((state.grouping.grandTotal || state.grouping.subtotals) && dimensions.length === 0) {
    bag.info('ignored', 'Sin dimensiones no se calculan totales ni subtotales.', {
      path: 'grouping',
    });
  }
  if (dimensions.length === 0 && measures.length === 0) {
    bag.info('empty-analysis', 'El analisis no tiene dimensiones ni medidas.');
  }

  if (bag.hasErrors) {
    return err(new AnalysisPlanError('No se puede planificar el analisis.', bag.items));
  }

  // Linaje por campo
  const lineage: PlannedLineage[] = [
    ...dimensions.map((d, i) => lineageOf(d.key, 'dimension', d.dimensionId, dimensionClosures[i])),
    ...measures.map((m, i) => lineageOf(m.key, 'measure', m.measureId, measureClosures[i])),
    ...(comparison?.outputs ?? []).map((output) => {
      const measure = measures.find((m) => m.key === output.measureKey);
      const closures = [
        ...(measure ? [resolver.resolve({ kind: 'measure', id: measure.measureId })] : []),
        ...(comparisonClosure ? [comparisonClosure] : []),
      ];
      return lineageOf(output.key, 'comparison', measure?.measureId ?? '', mergeClosures(closures));
    }),
  ];

  // Columnas fisicas y calculadas necesarias
  const allClosures = mergeClosures([...queryClosures, ...joinClosures, ...ruleClosures]);
  const columns: PlannedColumn[] = [];
  for (const columnId of allClosures.columnIds) {
    const resolved = index.getColumn(columnId);
    if (!resolved || isCalculatedColumn(resolved.column)) continue;
    columns.push({
      id: columnId,
      tableId: resolved.table.id,
      sourceColumn: resolved.column.sourceColumn,
      dataType: resolved.column.dataType,
    });
  }
  const calculatedColumns: PlannedCalculatedColumn[] = [];
  for (const columnId of allClosures.calculatedColumnIds) {
    const resolved = index.getColumn(columnId);
    if (!resolved || !isCalculatedColumn(resolved.column)) continue;
    calculatedColumns.push({
      id: columnId,
      tableId: resolved.table.id,
      dataType: resolved.column.dataType,
      expression: resolved.column.expression,
      dependsOn: collectExpressionReferences(resolved.column.expression).columnIds,
    });
  }

  const tables: PlannedTable[] = joinPlan.tableIds.map((tableId) => ({
    id: tableId,
    datasetTableId: index.getTable(tableId)?.datasetTableId ?? tableId,
    role: tableId === joinPlan.baseTableId ? 'base' : 'joined',
  }));

  const shapeDimensions: AnalysisDimension[] = dimensions.map((d) => ({
    key: d.key,
    dimensionId: d.dimensionId,
    ...(d.timeGrain === null ? {} : { timeGrain: d.timeGrain }),
  }));

  const body: Omit<QueryPlan, 'fingerprint'> = {
    version: QUERY_PLAN_VERSION,
    semanticModelId: model.id,
    analysisStateId: state.id,
    modelFingerprint: modelFingerprint(model),
    baseTableId: joinPlan.baseTableId,
    tables,
    columns,
    calculatedColumns,
    dimensions,
    measures,
    joins: joinPlan.joins,
    filters,
    ruleFilters,
    grouping: {
      dimensionKeys: dimensions.map((d) => d.key),
      rollupKeys: [...state.grouping.rollupKeys],
      grandTotal: state.grouping.grandTotal,
      subtotals: state.grouping.subtotals,
    },
    sorting: sortingResolution.sorting,
    pagination: paginationResolution.pagination,
    comparison,
    lineage,
    presentation: {
      visualization: state.visualization,
      interaction: state.interaction,
      layout: state.layout,
      shape: describeQueryShape({ dimensions: shapeDimensions, measures: state.measures }, index),
    },
    diagnostics: bag.items,
  };
  return ok({ ...body, fingerprint: queryPlanFingerprint(body) });
}

/** Tabla hogar de una medida: la declarada, la de su columna o la de sus dependencias. */
function homeTableId(
  measure: ModelMeasure,
  index: SemanticModelIndex,
  visited = new Set<string>(),
): string | null {
  if (visited.has(measure.id)) return null;
  visited.add(measure.id);
  if (measure.tableId !== undefined) return measure.tableId;
  if (!isCalculatedMeasure(measure)) {
    return measure.columnId === undefined
      ? null
      : (index.getColumn(measure.columnId)?.table.id ?? null);
  }
  const refs = collectExpressionReferences(measure.expression);
  for (const measureId of refs.measureIds) {
    const dependency = index.getMeasure(measureId);
    const tableId = dependency ? homeTableId(dependency, index, visited) : null;
    if (tableId !== null) return tableId;
  }
  for (const columnId of refs.columnIds) {
    const tableId = index.getColumn(columnId)?.table.id;
    if (tableId !== undefined) return tableId;
  }
  return null;
}

function lineageOf(
  key: string,
  kind: PlannedLineage['kind'],
  objectId: string,
  closure: DependencyClosure | undefined,
): PlannedLineage {
  const source = closure ?? mergeClosures([]);
  return {
    key,
    kind,
    objectId,
    measureIds: source.measureIds,
    calculatedColumnIds: source.calculatedColumnIds,
    columnIds: source.columnIds,
    tableIds: source.tableIds,
  };
}

function mergeClosures(closures: readonly DependencyClosure[]): DependencyClosure {
  return {
    measureIds: unique(closures.flatMap((closure) => closure.measureIds)),
    calculatedColumnIds: unique(closures.flatMap((closure) => closure.calculatedColumnIds)),
    columnIds: unique(closures.flatMap((closure) => closure.columnIds)),
    tableIds: unique(closures.flatMap((closure) => closure.tableIds)),
  };
}

const unique = <T>(items: readonly T[]): T[] => [...new Set(items)];

/** Clasifica una incidencia del validador del modelo en un codigo del planificador. */
function modelIssueDiagnostic(detail: ErrorDetail): AnalysisDiagnostic {
  let code: AnalysisDiagnosticCode = 'invalid-model';
  if (detail.path === 'calculatedMeasures' || detail.path === 'calculatedColumns') {
    code = 'cyclic-dependency';
  } else if (detail.path.endsWith('.aggregation')) {
    code = 'invalid-aggregation';
  } else if (detail.path.endsWith('.expression') || detail.path.endsWith('.filter')) {
    code = 'invalid-expression';
  } else if (detail.message.includes('inexistente')) {
    code = 'missing-reference';
  }
  return {
    severity: 'error',
    code,
    path: detail.path.length > 0 ? `model.${detail.path}` : 'model',
    message: detail.message,
  };
}
